import { useEffect } from 'react';

import classNames from 'classnames';

import { ExternalLink } from '@uikit/components/ExternalLink';

import { RewardPool } from '@shared/api/types/rewardPools';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { MarkCheck } from '@ui/uikit/components/icons/mono';

import { useRequirements } from '../../model/useRequirements';
import { VerifyRequirementButton } from './buttons/VerifyRequirementButton/VerifyRequirementButton';
import { List } from './List/List';
import { ListItem } from './List/ListItem';
import { ListItemButton } from './List/ListItemButton';
import { ListWrapper } from './List/ListWrapper';

type Props = {
  rewardPool: RewardPool;
  hasBorders?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
};

export const RequirementsList = ({
  rewardPool,
  showHeader = true,
  showFooter = true,
  hasBorders = true,
}: Props) => {
  const { requirements, hasRequirements, query } = useRequirements(rewardPool.id);
  const { refetch } = query;
  const { isAuth } = useAuth();

  const qualifiedCount = requirements.reduce(
    (acc, requirement) => acc + Number(requirement.qualified),
    0,
  );

  useEffect(() => {
    void refetch();
  }, [isAuth]);

  return (
    <>
      <ListWrapper
        header={
          showHeader && (
            <div className="flex xl:justify-between max-xl:flex-col gap-4 xl:gap-6">
              <h2 className="heading-h2-adaptive">Pool Requirements</h2>

              <span className="badge badge-lg min-h-10 font-semibold max-md:w-full md:max-w-[22.5rem] xl:max-w-none">
                <MarkCheck
                  className={classNames('badge-icon', {
                    'text-base-text-green': qualifiedCount === requirements.length,
                    'text-base-text-quaternary': qualifiedCount !== requirements.length,
                  })}
                />
                {qualifiedCount} / {requirements.length} Requirements met
              </span>
            </div>
          )
        }
        showFooter={showFooter}
      >
        <List
          isEmpty={!hasRequirements}
          query={query}
          hasBorders={hasBorders}
          data-qa="requirements_list"
        >
          {requirements.map((requirement, index) => {
            return (
              <ListItem
                isLastItem={index === requirements.length - 1}
                key={requirement.id}
                data-qa={`requirement-id_${requirement.id}`}
                isDisabled={requirement.qualified}
                label={requirement.title}
                description={requirement.description}
                buttons={
                  <>
                    {requirement.action && !requirement.qualified && (
                      <ListItemButton variant="secondary">
                        <ExternalLink href={requirement.action.href} className="w-full">
                          {requirement.action.label}
                        </ExternalLink>
                      </ListItemButton>
                    )}
                    <VerifyRequirementButton requirement={requirement} refetch={refetch} />
                  </>
                }
              />
            );
          })}
        </List>
      </ListWrapper>
    </>
  );
};
